import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db928grk13ra8mig90fg";

const CONTACTS = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@qinwenjiejie.com" },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 24 小时内回复" },
  { icon: "ri-map-pin-line", label: "内容方向", value: "影视资讯 · 剧情解析 · 角色分析" }
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentTitle(
    "联系反馈 - 亲吻姐姐影视剧资讯与剧情解析",
    "联系亲吻姐姐，提交你想推荐的影视作品、剧情解析建议或内容反馈。我们会在工作日尽快回复你。"
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString()
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg ? `提交失败：${serverMsg}` : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-14 lg:px-8">
          <Breadcrumb items={[{ label: "联系反馈" }]} />
          <h1 className="mt-5 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">联系反馈</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            有想推荐的作品、想看的剧情解析，或对内容有建议？欢迎留下你的反馈，我们会认真阅读每一条留言。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950">联系方式</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              你可以通过右侧的反馈表单联系我们，也可以直接发送邮件。我们更倾向于在邮件中附上具体作品名称，以便更快定位。
            </p>

            <div className="mt-7 flex flex-col gap-4">
              {CONTACTS.map((item) => (
                <div key={item.label} className="flex items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl bg-accent-50 p-5">
              <p className="text-xs leading-relaxed text-accent-800">
                <i className="ri-information-line mr-1"></i>
                说明：本站内容均为演示示例，人物与作品为虚构设定。你的反馈仅用于内容改进，我们不会对外公开。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">提交反馈</h2>
              <p className="mt-2 text-sm text-foreground-600">带 * 的为必填项。</p>

              <form
                data-readdy-form
                id="contact-feedback-form"
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-sm font-medium text-foreground-800">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收我们的回复"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="text-sm font-medium text-foreground-800">
                    反馈内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你想推荐的影视作品或建议（最多 500 字）"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                  ></textarea>
                  <p className="text-xs text-foreground-400">最多 500 字。</p>
                </div>

                <input
                  className="form-hp-gate"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交反馈
                    </>
                  )}
                </button>

                {status === "success" && (
                  <div className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-xl text-secondary-600"></i>
                    <div>
                      <p className="text-sm font-medium text-secondary-900">感谢你的反馈！</p>
                      <p className="mt-1 text-xs leading-relaxed text-secondary-800">
                        我们已经收到你的留言，会在工作日尽快回复。感谢你支持亲吻姐姐。
                      </p>
                    </div>
                  </div>
                )}

                {status === "error" && (
                  <div className="flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4">
                    <i className="ri-error-warning-fill mt-0.5 text-xl text-accent-600"></i>
                    <p className="text-sm leading-relaxed text-accent-800">{formError || "提交失败，请稍后重试。"}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}